<template>
  <div class="flex-column">
    <div
      class="source-content file-dropzone"
      @dragover.prevent.stop="startDragover"
    >
      <div
        v-if="isDragging"
        @drop.prevent="handleDrop"
        @dragleave="isDragging = false"
        class="file-dropzone__droparea"
      >
        <div class="droparea__help-text">Drag + drop image or video to upload</div>
      </div>

      <div class="filter-bar flex items-center">
        <search-filter
          class="flex-grow"
          :value="assetSearch"
          searchOnly
          data-test="producer-designLibrary-searchFilter"
          @input="searchAssets"
        />
        <div class="design-library__filter-wrap">
          <button
            class="tb-btn tb-btn-link low-em"
            data-test="producer-designLibrary-filters"
            @click="isFilterPanelOpen = !isFilterPanelOpen"
          >
            <span class="fak fa-bars-filter-regular"></span>
          </button>

          <div
            v-if="isFilterPanelOpen"
            class="design-library__filter-panel"
            data-test="producer-designLibrary-filtersPanel"
          >
            <ul
              :id="`listbox-${_uid}`"
              class="design-library__optionList"
              role="listbox"
              aria-multiselectable="true"
              aria-label="Option list"
            >
              <li
                class="design-library__optionListItem"
                role="option"
                aria-label="image"
                :aria-selected="selected.includes('image')"
                :data-test="`producer-searchFilter-option:image`"
              >
                <div class="tb-checkbox">
                  <input
                    v-model="assetTypesModel"
                    value="image"
                    type="checkbox"
                    id="image-filter"
                    :data-test="`producer-searchFilter-checkBox:image`"
                  />

                  <label class="checkbox" for="image-filter">Image</label>
                </div>
              </li>
              <li
                class="design-library__optionListItem"
                role="option"
                aria-label="video"
                :aria-selected="selected.includes('video')"
                :data-test="`producer-searchFilter-option:video`"
              >
                <div class="tb-checkbox">
                  <input
                    v-model="assetTypesModel"
                    value="video"
                    type="checkbox"
                    id="video-filter"
                    :data-test="`producer-searchFilter-checkBox:video`"
                  />

                  <label class="checkbox" for="video-filter">Video</label>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <button
          class="tb-btn tb-btn-link low-em"
          data-test="producer-designLibrary-sort"
          @click="toggleAssetSortDir"
        >
          <span
            class="fa"
            :class="{
              'fa-sort-amount-up-alt': assetSortDir === 'asc',
              'fa-sort-amount-down': assetSortDir === 'desc',
            }"
          />
        </button>
      </div>

      <!-- Breadcrumbs -->
      <div
        v-if="assetDirectory"
        class="breadcrumbs margin-bottom-sm"
      >
        <button
          class="tb-btn-link high-em"
          data-test="producer-designLibrary-rootDirectory"
          @click="changeDirectory(null)"
        >
          Library
        </button>

        <template v-if="assetDirectory.parent">
          <span class="slash margin-left-xxs margin-right-xxs">/</span>
          <button
            class="tb-btn-link high-em"
            data-test="producer-designLibrary-rootDirectory"
            @click="changeDirectory(assetDirectory.parent._id)"
          >
            ...
          </button>
        </template>

        <span class="slash margin-left-xxs margin-right-xxs">/</span>

        <span
          class="tagboard-title"
          :title="assetDirectory.title"
          :data-test="`producer-designLibrary-curatedContent-currentBoard:${assetDirectory._id}`"
        >
          {{ assetDirectory.title }}
        </span>
      </div>

      <div
        ref="sourceList"
        class="source-list"
        data-test="producer-designLibrary-displayElements-list"
        >
        <!-- Folders -->
        <button
          v-for="d in assetDirectories"
          :key="d._id"
          class="design-library__directory ellipsis"
          @click="changeDirectory(d._id)"
        ><span class="far fa-folder margin-right-xxs" /> {{ d.title }}</button>

        <div v-if="isUploading"
          class="flex items-center content-center margin-top-md margin-bottom-md"
          data-test="producer-designLibrary-uploading"
        >
          <div class="text-center">
            <p>Uploading..</p>
            <span class="fas fa-spinner-third fa-spin fa-xl"></span>
          </div>
        </div>

        <!-- Assets -->
        <draggable
          v-model="assets"
          :clone="clone"
          :sort="false"
          animation="200"
          :group="{ name: 'timeline', pull: 'clone', put: false }"
          revert-on-spill="true"
          class="assets margin-top-sm"
          @start="startDrag"
          @end="$store.dispatch('clearDragType')"
        >
          <div
            v-for="(a, idx) in assets"
            :key="a._id"
            :data-asset-id="a._id"
            class="assets__asset"
            :class="{ 'assets__asset--selected': selected.includes(a) }"
          >
            <input
              type="checkbox"
              :id="a._id"
              class="assets__checkbox"
              :value="a"
              v-model="selected"
              :data-test="`producer-designLibrary-asset:${a._id}`"
            >
            <label
              class="assets__checkbox-label"
              :for="a._id"
              @click.exact="lastSelectedAssetIndex = idx"
              @click.shift.stop.prevent="selectRange(idx)"
            />

            <img
              :src="a.thumbnail_url || a.media_url"
              :alt="a.title"
              class="assets__thumbnail"
            />
            <div class="assets__info flex items-center content-between margin-top-xxs">
              <p class="margin-none ellipsis">{{ a.title }}</p>
              <button
                class="assets__preview-btn"
                @click="openAssetPreview(a)"
              ><span class="fa fa-search"/></button>
            </div>
          </div>
        </draggable>

        <div ref="loadTrigger" class="load-trigger">
          <div v-if="isLoading" class="loading loading--inline margin-top-sm" data-test="producer-designLibrary-loading">
            <i class="icon icon-spinner"></i>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="selected.length"
      class="flex content-end items-center padding-md"
      data-test="producer-designLibrary-selected"
    >
      <p class="text-primary margin-bottom-none margin-right-sm" data-test="producer-designLibrary-selected-count">{{ selected.length }} selected</p>

      <button
        class="tb-btn tb-btn-contained medium-em margin-left-md"
        :class="{'disabled': !selected.length }"
        :disabled="!selected.length"
        data-test="producer-designLibrary-selected-addSelectedToPlaylist"
        @click="addSelectedToPlaylist('assets')"
      >
        Add to Playlist
      </button>
    </div>

    <!-- Preview Modal -->
    <modal
      v-if="isPreviewModalOpen"
      class="modal--large preview-modal"
      @close="closePreviewModal"
    >
      <template #modal-header="{ close }">
        <div class="title">Asset Preview</div>

        <button
          class="modal-close tb-btn tb-btn-text sm low-em"
          data-test="producer-designLibrary-closePreview"
          @click="close"
        >
          <span class="icon icon-cross" />
        </button>
      </template>
      <template #modal-body>
        <img
          v-if="previewAsset.asset_type === 'image'"
          :src="previewAsset.media_url"
          :alt="previewAsset.title"
          class="preview-modal__image"
        />

        <video
          v-else-if="previewAsset.asset_type === 'video'"
          :src="previewAsset.media_url"
          :alt="previewAsset.title"
          class="preview-modal__video"
          controls
        ></video>
      </template>
      <template #modal-footer>
        <button
          class="tb-btn tb-btn-contained medium-em"
          data-test="producer-designLibrary-addToPlaylist"
          @click="addToPlaylist(previewAsset, 'asset'); closePreviewModal();"
        >
          Add to Playlist
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { pick } from 'lodash-es';
import * as uuid from 'uuid';

import { getPresignedUrl, createAsset } from '@/js/services/assets';

import tabMixins from '@/js/mixins/tab-mixins';
import Modal from '@/js/components/modal/Modal.vue';
import SearchFilter from '@/js/shared/SearchFilter.vue';

export default {
  name: 'DesignLibrary',
  mixins: [tabMixins],
  components: {
    Modal,
    SearchFilter,
  },
  data() {
    return {
      isPreviewModalOpen: false,
      previewAsset: null,
      isFilterPanelOpen: false,
      lastSelectedAssetIndex: null,
      isDragging: false,
      isUploading: false,
    };
  },
  computed: {
    ...mapState('assets', [
      'assets',
      'assetDirectory',
      'assetDirectories',
      'assetSearch',
      'assetSortDir',
      'assetTypes',
    ]),
    ...mapGetters('assets', [
      'isLoading',
      'hasMoreAssets',
    ]),

    ownerId() {
      return this.$store.state.production.production.owner;
    },

    assetTypesModel: {
      get() {
        return this.assetTypes;
      },
      set(v) {
        this.setAssetTypes(v);
      },
    },
  },
  watch: {
    assets() {
      if (!this.$refs.sourceList || !this.assets.length) {
        return;
      }

      if (this.$refs.sourceList.scrollHeight <= this.$refs.sourceList.offsetHeight) {
        // List isn't scrollable, so try to load more right away
        // Otherwise the intersection observer will never trigger it's callback
        this.loadMore();
      }
    },
  },
  methods: {
    ...mapActions('assets', [
      'getAssets',
      'setDirectory',
      'searchAssets',
      'toggleAssetSortDir',
      'setAssetTypes',
      'getNextPage',
      'addUploadedAsset',
    ]),

    changeDirectory(id) {
      this.setDirectory(id);
    },
    openAssetPreview(asset) {
      this.previewAsset = asset;
      this.isPreviewModalOpen = true;
    },
    closePreviewModal() {
      this.isPreviewModalOpen = false;
      this.previewAsset = null;
    },
    clone(original) {
      const clone = pick(original, ['_id', 'title', 'media_url', 'thumbnail_url', 'asset_type']);
      clone.layout = 'asset';
      clone.timeline_id = uuid.v1();
      clone.displayTime = 5000;
      clone.transition = 'Cut'; // legacy but keep for backwards compatibility during testing
      clone.posts = []; // needed to pass timeline item validation
      clone.fields = {};

      return clone;
    },
    startDrag(e) {
      const { item } = e;
      const { assetId } = item.dataset;

      const grabGroup = !!this.selected.find((t) => t._id === assetId);

      this.$store.dispatch('setDragType', {
        type: 'asset',
        group: grabGroup ? this.selected.map(this.clone) : null,
      });
    },
    selectRange(assetIdx) {
      if (this.lastSelectedAssetIndex === null) {
        this.lastSelectedAssetIndex = assetIdx;
      }

      const start = Math.min(this.lastSelectedAssetIndex, assetIdx);
      const end = Math.max(this.lastSelectedAssetIndex, assetIdx);

      this.selected = this.assets.slice(start, end + 1);
    },

    startDragover(e) {
      if (e.dataTransfer.types.includes('Files')) {
        this.isDragging = true;
      }
    },

    async handleDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      const currentDirectory = this.assetDirectory?._id;

      // file utilities (lifted from tgb-app)
      const getUniqueFilename = (filename) => {
        const formattedFilename = filename.replace(/\s/g, '_');
        const [, name, extension] = /(?:(.*)\.([^.]+))?$/.exec(formattedFilename);
        const hex = Array.from({ length: 5 }).map(() => Math.floor(Math.random() * 16).toString(16)).join('');
        return `${name}-${hex}.${extension}`;
      };

      const getFileExtension = (filename) => /(?:\.([^.]+))?$/.exec(filename)[1];

      const droppedFiles = Array.from(e.dataTransfer.files);

      const validFiles = droppedFiles.filter((file) => {
        if (![ 'image', 'video' ].includes(file.type.split('/')[0])) {
          this.errorToast(`${file.name} is not a valid image or video file`);
          return false;
        }

        return true;
      });

      this.isUploading = true;
      await Promise.allSettled(validFiles.map(async (file) => {
        const signedUrlParams = {
          filename: getUniqueFilename(file.name),
          contentType: file.type,
          type: file.type.split('/')[0],
        };

        try {
          // Get data for signed url
          const { url, fields } = await getPresignedUrl(signedUrlParams);

          const s3Response = await fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type,
            },
            body: file,
          });

          if (!s3Response.ok) {
            throw new Error(`Error uploading file ${file.name} to s3`);
          }

          // create asset
          const meta = {
            type: getFileExtension(file.name),
            size: file.size,
            content_type: file.type,
          };

          if (signedUrlParams.type === 'image') {
            meta.height = file.height;
            meta.width = file.width;
          }

          const newAsset = {
            owner: this.ownerId,
            title: file.name.replace(/\s/g, '_'),
            tags: [meta.type, signedUrlParams.type],
            meta,
            media_url: `${process.env.ASSET_MEDIA_URL}${fields.Key}`,
            s3_key: fields.Key,
            asset_type: signedUrlParams.type,
            directory: currentDirectory,
          };

          const newId = await createAsset(newAsset);
          newAsset._id = newId;

          this.addUploadedAsset(newAsset);

          return newAsset;
        } catch (error) {
          this.errorToast(`Error uploading ${file.name}`);
        }
      }));

      this.isUploading = false;
    },
    loadMore() {
      if (!this.isLoading && this.hasMoreAssets) {
        this.getNextPage();
      }
    },
  },
  created() {
    this.getAssets();
  },
  mounted() {
    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },
  beforeDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  },
};
</script>

<style lang="scss" scoped>
.design-library {
  padding: 10px 20px;
  flex: 1 0 0;

  &__directory {
    display: block;
    width: 100%;
    padding: 8px 13px;
    text-align: left;
    font-size: 12px;
    color: #fff;
    background-color: #414141;
    border: none;
    border-radius: 6px;

    & .fa-folder {
      color: variables.$color-on-surface-medium;
    }

    & + & {
      margin-top: 10px;
    }
  }

  &__filterWrap {
    position: relative;
    overflow: visible;
  }

  &__filter-panel {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 100;
    background-color: variables.$color-02dp;
    padding: 8px;
    width: auto;
    max-width: 100%;
    max-height: 400px;
    display: flex;
    flex-direction: column;
    border: 1px solid variables.$color-on-surface-medium;
    border-radius: 3px;
  }

  &__optionList {
    list-style: none;
    flex: 1;
    padding: 0;
    margin: 0;
    overflow-y: auto;
  }

  &__optionListItem {
    & + & {
      margin-top: 4px;
    }
  }
}

.assets {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 14px;

  &__asset {
    border: 1px solid variables.$color-on-surface-medium;
    border-radius: 3px;
    padding: 5px;
    position: relative;

    &--selected {
      border-color: variables.$color-primary;
    }

    &.sortable-ghost {
      max-height: 50px;
      margin: 0;
      padding: 10px;
      background-color: variables.$color-06dp;
      border: none;
      display: flex;
      align-items: center;
      justify-content: flex-start;

      & > :not(img) {
        display: none;
      }

      & > .assets__info {
        display: block !important;
        margin-left: 8px;

        & > .assets__preview-btn {
          display: none;
        }
      }

      & > img {
        width: 70px;
        object-fit: contain;
        background-color: transparent;
      }
    }
  }

  &__checkbox {
    position: absolute;
    margin: 0;
    opacity: 0;
  }

  &__checkbox:checked + &__checkbox-label::before {
    content: "\f00c"; // fa-check
    background-color: variables.$color-primary;
  }

  &__checkbox-label {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    &::before {
      content: '';
      font-family: 'Font Awesome 5 Pro';
      font-weight: 300;
      color: black;
      font-size: 10px;
      position: absolute;
      top: 7px;
      left: 7px;
      display: inline-block;
      width: 12px;
      height: 12px;
      background-color: #fff;
      border-radius: 3px;
      cursor: pointer;
      text-align: center;
      vertical-align: middle;
      line-height: 12px;
    }
  }

  &__thumbnail {
    width: 100%;
    max-width: 100%;
    object-fit: contain;
    aspect-ratio: 16/9;
    background-color: #d9d9d9;
    border-radius: 3px;
  }

  &__preview-btn {
    background-color: transparent;
    border: none;
    color: variables.$color-on-surface-medium;
    z-index: 1;
  }
}

.preview-modal {
  &__image,
  &__video {
    width: 100%;
    aspect-ratio: 16/9;
    border-radius: 3px;
    background-position: 0px 0px, 10px 10px;
    background-size: 20px 20px;
    background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%),linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
    background-blend-mode: normal, difference, normal;
  }

  &__image {
    object-fit: contain;
  }
}

.file-dropzone {
  position: relative;

  &__droparea {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #fff;
    border-radius: 3px;
    z-index: 100;
  }

  &__help-text {
    color: #fff;
    font-size: 24px;
    max-width: 80px;
    font-weight: 700;
  }
}

</style>
